import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = "Authentic Makrana Marble - Premium Quality Marble Handicrafts, Temples & Slabs",
  description = "Discover genuine Makrana marble handicrafts, temple carvings, mosque architecture, and premium quality slabs. Direct from Rajasthan's finest marble artisans with 20+ years of craftsmanship experience.",
  keywords = "makrana marble, marble handicrafts, temple carving, mosque architecture, marble slabs, rajasthan marble, marble inlay work, authentic marble, premium marble, marble artisans",
  image = "/images/site/makrana-marble-og.jpg",
  url = "https://makrana-marble.com",
  type = "website",
  siteName = "Makrana Marble",
  locale = "en_US",
  canonicalUrl,
  structuredData,
  noIndex = false,
  breadcrumbs = [],
  article = null,
  product = null,
  businessHours = null
}) => {
  const fullTitle = title.includes('Makrana Marble') ? title : `${title} | Makrana Marble`;
  const fullUrl = url + (canonicalUrl || '');

  // Enhanced default structured data with multiple schemas
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${url}/#organization`,
        "name": "Makrana Marble",
        "description": description,
        "url": url,
        "logo": {
          "@type": "ImageObject",
          "url": `${url}/images/site/logo.png`,
          "width": 200,
          "height": 200
        },
        "sameAs": [
          "https://www.facebook.com/makranamarble",
          "https://www.instagram.com/makranamarble",
          "https://wa.me/917014351132"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-7014351132",
          "contactType": "customer service",
          "availableLanguage": ["English", "Hindi"],
          "areaServed": "IN"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN",
          "addressRegion": "Rajasthan",
          "addressLocality": "Makrana"
        }
      },
      {
        "@type": "WebSite",
        "@id": `${url}/#website`,
        "url": url,
        "name": siteName,
        "description": description,
        "publisher": {
          "@id": `${url}/#organization`
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${url}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  // Add breadcrumb schema if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbSchema = {
      "@type": "BreadcrumbList",
      "@id": `${fullUrl}/#breadcrumbs`,
      "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": breadcrumb.name,
        "item": `${url}${breadcrumb.url}`
      }))
    };
    defaultStructuredData["@graph"].push(breadcrumbSchema);
  }

  // Add article schema if provided
  if (article) {
    const articleSchema = {
      "@type": "Article",
      "@id": `${fullUrl}/#article`,
      "headline": article.headline || title,
      "description": description,
      "datePublished": article.datePublished,
      "dateModified": article.dateModified || article.datePublished,
      "author": {
        "@type": "Organization",
        "@id": `${url}/#organization`
      },
      "publisher": {
        "@id": `${url}/#organization`
      },
      "image": {
        "@type": "ImageObject",
        "url": `${url}${image}`,
        "width": 1200,
        "height": 630
      }
    };
    defaultStructuredData["@graph"].push(articleSchema);
  }

  // Add product schema if provided
  if (product) {
    const productSchema = {
      "@type": "Product",
      "@id": `${fullUrl}/#product`,
      "name": product.name,
      "description": product.description || description,
      "image": product.images?.map(img => `${url}${img}`) || [`${url}${image}`],
      "category": product.category,
      "material": "Marble",
      "brand": {
        "@type": "Brand",
        "name": "Makrana Marble"
      },
      "manufacturer": {
        "@id": `${url}/#organization`
      },
      "offers": product.price ? {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@id": `${url}/#organization`
        }
      } : undefined
    };
    defaultStructuredData["@graph"].push(productSchema);
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Makrana Marble" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content={noIndex ? "noindex,nofollow" : "index,follow"} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={fullUrl} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image.startsWith('http') ? image : `${url}${image}`} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image.startsWith('http') ? image : `${url}${image}`} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="IN-RJ" />
      <meta name="geo.placename" content="Makrana, Rajasthan" />
      <meta name="geo.position" content="27.0467;74.4414" />
      <meta name="ICBM" content="27.0467, 74.4414" />
      
      {/* Favicon and Touch Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;